import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const highlights = [
  {
    icon: BarChart3,
    title: 'Real-time financial overview',
    description: 'Track dues, payments and budgets at a glance.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure and compliant',
    description: 'Bank-grade encryption with full audit trails.',
  },
  {
    icon: Sparkles,
    title: 'Built for treasurers',
    description: 'Streamlined workflows tailored to your unit.',
  },
];

export default function LoginPage() {
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      navigate(returnTo);
    }
  }, [currentUser, navigate, returnTo]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Decorative background blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-emerald-300/30 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-teal-300/30 blur-3xl"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        {/* Top bar */}
        <header className="flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 transition-opacity hover:opacity-90"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30">
              <Wallet className="h-5 w-5 text-white" aria-hidden />
            </div>
            <div className="text-left">
              <div className="text-base font-bold text-slate-900">Treasurer</div>
              <div className="text-xs text-slate-500">Financial Management</div>
            </div>
          </Link>
          <span className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700/70 sm:inline">
            Masonic Tools
          </span>
        </header>

        {/* Main split */}
        <main className="mt-6 grid flex-1 grid-cols-1 gap-10 lg:mt-12 lg:grid-cols-2 lg:gap-16">
          {/* Branding panel */}
          <section className="hidden flex-col justify-between rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 p-10 text-white shadow-2xl shadow-emerald-900/20 lg:flex">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white/90 backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Treasurer Suite
              </span>
              <h1 className="mt-8 text-4xl font-bold leading-tight tracking-tight">
                Manage your lodge finances with confidence.
              </h1>
              <p className="mt-4 max-w-md text-base text-emerald-50/90">
                A modern, secure platform that brings dues, payments and reporting
                together in one place.
              </p>
            </div>

            <ul className="mt-10 space-y-5">
              {highlights.map(({ icon: Icon, title, description }) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-inset ring-white/20 backdrop-blur">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{title}</p>
                    <p className="text-sm text-emerald-50/80">{description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-3 border-t border-white/10 pt-6 text-xs text-emerald-50/70">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              <span>Encrypted in transit and at rest. SOC 2 aligned controls.</span>
            </div>
          </section>

          {/* Form panel */}
          <section className="flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="rounded-3xl border border-emerald-100 bg-white/90 p-8 shadow-xl shadow-emerald-900/10 backdrop-blur sm:p-10">
                <div className="mb-8 text-center sm:text-left">
                  <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                    Welcome back
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Sign in to manage your unit finances.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div className="space-y-1.5">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-slate-700"
                    >
                      Email address
                    </label>
                    <div className="relative">
                      <Mail
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                        aria-hidden
                      />
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="you@example.com"
                        required
                        className="block h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="password"
                        className="text-sm font-medium text-slate-700"
                      >
                        Password
                      </label>
                      <Link
                        to="/reset-password"
                        className="text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-800"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                        aria-hidden
                      />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Enter your password"
                        required
                        className="block h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-10 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 text-sm font-semibold text-white shadow-lg shadow-emerald-600/25 transition hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 flex items-center gap-3 text-xs text-slate-400">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="uppercase tracking-wider">or</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                <p className="mt-6 text-center text-sm text-slate-600">
                  Don&apos;t have an account?{' '}
                  <Link
                    to="/signup"
                    className="font-semibold text-emerald-700 hover:text-emerald-800"
                  >
                    Create one
                  </Link>
                </p>
              </div>

              <p className="mt-6 text-center text-xs text-slate-500">
                By signing in you agree to our{' '}
                <Link to="/terms" className="font-medium text-slate-600 hover:text-slate-800">
                  Terms
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="font-medium text-slate-600 hover:text-slate-800">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
